import React, { useEffect, useRef } from 'react';

import { Link } from 'react-router-dom';

import {
  CheckCircleFilled,
  ClockCircleOutlined,
  CloseCircleFilled,
  RetweetOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import {
  AsyncJobName,
  AsyncJobStatus,
  AsyncJobStep,
  AsyncJobStepName,
  AsyncJobStepStatus,
} from '@recurrency/core-api-schema/dist/asyncJobs/common';
import { PostgresNotification } from '@recurrency/core-api-schema/dist/common/enums';
import { ColumnType } from 'antd/lib/table';
import { colors } from 'theme/colors';

import { Alert } from 'components/Alert';
import { AsyncButton } from 'components/Button/AsyncButton';
import { Container } from 'components/Container';
import { DividerLine } from 'components/DividerLine';
import { FlexSpace } from 'components/FlexSpace';
import { CenteredError, CenteredLoader } from 'components/Loaders';
import { PageHeader } from 'components/PageHeader';
import { Table } from 'components/Table';
import { Tooltip } from 'components/Tooltip';
import { EllipsisTruncatedScrollableTooltip } from 'components/Typography/EllipsisTruncatedLabel';

import { useCoreApi } from 'hooks/useApi';
import { useGlobalApp } from 'hooks/useGlobalApp';

import { coreApiFetch, watchAsyncJob } from 'utils/api';
import { formatDuration } from 'utils/formatting';
import { IdPathParams, routes, usePathParams } from 'utils/routes';
import { asKeyOf, sortableDateColumn, sortableStringColumn } from 'utils/tables';
import { track, TrackEvent } from 'utils/track';

import { JobStatusBadge } from './JobStatusBadge';
import { StepStatusProgressBar } from './StepStatusProgressBar';
import {
  calcDurationMsFromDates,
  formatJobName,
  formatJobStepName,
  formatMinMaxUpdatesLabel,
  iconStyle,
} from './utils';

export const ExportJobDetailsPage = () => {
  const { id: jobId } = usePathParams<IdPathParams>();
  const { activeUser } = useGlobalApp();
  const { asyncJob, isLoading, error, reload: reloadJob } = useWatchAsyncJob(jobId);
  const steps = asyncJob?.steps || [];
  // the item-location rendering is for automated runs only; user-initiated exports keep the raw payload
  const isAutomatedMinMaxJob = asyncJob?.name === AsyncJobName.AutomatedMinMaxUpdate;
  const stepStatusCounts = {
    total: steps.length,
    [AsyncJobStepStatus.Pending]: steps.filter((step) => step.status === AsyncJobStepStatus.Pending).length,
    [AsyncJobStepStatus.Success]: steps.filter((step) => step.status === AsyncJobStepStatus.Success).length,
    [AsyncJobStepStatus.Running]: steps.filter((step) => step.status === AsyncJobStepStatus.Running).length,
    [AsyncJobStepStatus.Failed]: steps.filter((step) => step.status === AsyncJobStepStatus.Failed && !step.nonCritical)
      .length,
    nonCriticalFailed: steps.filter((step) => step.status === AsyncJobStepStatus.Failed && step.nonCritical).length,
  };

  const renderJobStepStatus = (status: AsyncJobStepStatus, step: AsyncJobStep) => {
    if (status === AsyncJobStepStatus.Pending) {
      return (
        <Tooltip title="Pending">
          <ClockCircleOutlined style={{ ...iconStyle, color: colors.neutral[500] }} />
        </Tooltip>
      );
    }
    if (status === AsyncJobStepStatus.Success) {
      return (
        <Tooltip title="Success">
          <CheckCircleFilled style={{ ...iconStyle, color: colors.success[500] }} />
        </Tooltip>
      );
    }
    if (status === AsyncJobStepStatus.Running) {
      return (
        <Tooltip title="Running">
          <SyncOutlined spin style={{ ...iconStyle, color: colors.info[500] }} />
        </Tooltip>
      );
    }
    if (status === AsyncJobStepStatus.Failed && step.nonCritical) {
      return (
        <Tooltip title="Non-critical sync failed - Background sync will be auto-retry within 15 minutes">
          <CheckCircleFilled style={{ ...iconStyle, color: colors.ocean[500] }} />
        </Tooltip>
      );
    }
    if (status === AsyncJobStepStatus.Failed && !step.nonCritical) {
      return (
        <Tooltip title="Failed">
          <CloseCircleFilled style={{ ...iconStyle, color: colors.danger[500] }} />
        </Tooltip>
      );
    }
    return <></>;
  };

  const columns: ColumnType<AsyncJobStep>[] = [
    {
      title: 'Status',
      dataIndex: asKeyOf<AsyncJobStep>('status'),
      width: 80,
      align: 'center',
      render: (status: AsyncJobStep['status'], step: AsyncJobStep) => renderJobStepStatus(status, step),
    },
    sortableStringColumn({
      title: 'Step',
      dataIndex: asKeyOf<AsyncJobStep>('name'),
      render: (name: AsyncJobStepName) => formatJobStepName(name),
    }),
    sortableDateColumn({
      title: 'Started',
      dataIndex: asKeyOf<AsyncJobStep>('startedAt'),
      withTimestamp: true,
      inLocalTime: true,
    }),
    sortableDateColumn({
      title: 'Finished',
      dataIndex: asKeyOf<AsyncJobStep>('finishedAt'),
      withTimestamp: true,
      inLocalTime: true,
    }),
    {
      title: 'Duration',
      dataIndex: asKeyOf<AsyncJobStep>('startedAt'),
      render: (_, record) => formatDuration(calcDurationMsFromDates(record.startedAt, record.finishedAt)),
      align: 'right',
    },
    {
      title: 'Request',
      dataIndex: asKeyOf<AsyncJobStep>('request'),
      width: 200,
      render: (request: Any, step: AsyncJobStep) => {
        if (!request) return '-';
        // an automated run's batch holds up to 100 item-locations, which is unreadable as raw JSON
        const minMaxUpdates =
          isAutomatedMinMaxJob && step.name === AsyncJobStepName.ExportERPMinMaxes
            ? request.payload?.updates
            : undefined;
        if (minMaxUpdates?.length) {
          return (
            <Tooltip
              title={
                <div
                  className={css`
                    white-space: pre-wrap;
                    max-height: 400px;
                    overflow: auto;
                  `}
                >
                  {formatMinMaxUpdatesLabel(minMaxUpdates)}
                </div>
              }
            >
              <span>
                {minMaxUpdates.length} item-location{minMaxUpdates.length === 1 ? '' : 's'}
              </span>
            </Tooltip>
          );
        }
        return (
          <EllipsisTruncatedScrollableTooltip
            maxChars={40}
            label={JSON.stringify(request.payload || request, null, 2)}
          />
        );
      },
    },
    {
      title: 'Response',
      dataIndex: asKeyOf<AsyncJobStep>('response'),
      width: 200,
      render: (response: AsyncJobStep['response']) =>
        !response ? (
          '-'
        ) : response.purchase_order_no ? (
          <Link to={routes.purchasing.purchaseOrderDetails(response.purchase_order_no)}>
            PO #{response.purchase_order_no}
          </Link>
        ) : response.purchaseOrderId ? (
          // sapb1 returns purchaseOrderId
          <Link to={routes.purchasing.purchaseOrderDetails(response.purchaseOrderId)}>
            PO #{response.purchaseOrderId}
          </Link>
        ) : response.transfer_no ? (
          <Link to={routes.purchasing.transferOrderDetails(response.transfer_no)}>TO #{response.transfer_no}</Link>
        ) : (
          <EllipsisTruncatedScrollableTooltip maxChars={40} label={JSON.stringify(response, null, 2)} />
        ),
    },
    {
      title: 'Error',
      dataIndex: asKeyOf<AsyncJobStep>('error'),
      width: 200,
      render: (err: string) => (err ? <EllipsisTruncatedScrollableTooltip maxChars={40} label={err} /> : '-'),
    },
  ];

  if (error) {
    return <CenteredError error={error} />;
  }

  if (isLoading || !asyncJob) {
    return <CenteredLoader />;
  }

  return (
    <Container>
      <PageHeader
        title={formatJobName(asyncJob.name)}
        copyable
        entity={{
          kind: 'Export Job',
          id: asyncJob.id,
          badge: (
            <FlexSpace direction="row" gap={8}>
              <JobStatusBadge status={asyncJob.status} />
              <StepStatusProgressBar statusCounts={stepStatusCounts} width={200} />
            </FlexSpace>
          ),
        }}
        headerActions={
          steps.some((step) => step.status === AsyncJobStepStatus.Failed) &&
          activeUser.isRecurrencyAdmin && (
            <AsyncButton
              onClick={async () => {
                await coreApiFetch(schemas.asyncJobs.postAsyncJobRetryFailedSteps, { pathParams: { jobId } });
                reloadJob();
                track(TrackEvent.ExportJobs_RetryFailedStepsClick, { jobId });
              }}
            >
              <RetweetOutlined /> Retry Failed Steps
            </AsyncButton>
          )
        }
      />
      {asyncJob.error && (
        <Alert
          className={css`
            margin-bottom: 16px;
          `}
          message="Error"
          description={asyncJob.error}
          type="error"
          showIcon
        />
      )}
      <DividerLine />
      <Table data={steps} columns={columns} rowKey="id" pageSize={50} />
    </Container>
  );
};

export function useWatchAsyncJob(jobId: string) {
  const {
    data: asyncJob,
    isLoading,
    error,
    reload,
  } = useCoreApi(schemas.asyncJobs.getAsyncJob, {
    pathParams: { jobId },
  });

  // track state in isWatchingRef so we only watch the job once if it is running
  const isWatchingRef = useRef<string>();
  const reloadTimeoutRef = useRef<number>();
  useEffect(() => {
    if (asyncJob && asyncJob.status === AsyncJobStatus.Running && isWatchingRef.current !== asyncJob.id) {
      isWatchingRef.current = asyncJob.id;
      watchAsyncJob(asyncJob.id, (notif) => {
        if (notif.event === PostgresNotification.AsyncJob || notif.event === PostgresNotification.AsyncJobStep) {
          // debounce calling reload as notifications can be chatty
          if (reloadTimeoutRef.current) clearTimeout(reloadTimeoutRef.current);
          reloadTimeoutRef.current = window.setTimeout(() => reload(), 2000);
        }
      });
    }
  }, [asyncJob, reload]);

  return { asyncJob, isLoading, error, reload };
}
